/**
 * Landing v3 — the phone's one floating Ka (user 2026-09-29): the Rive "Ka
 * floating" icon, laid out at its largest and scaled down so he stays crisp.
 *
 * - From the hero through the features and the tiles he rests in the corner,
 *   beside the floating button (Figma 116:885 / 116:1100) — except while he
 *   speaks the hero's line, when he glides to the centre over it (116:972)
 *   and back.
 * - From the agent chat on he is the desktop's Ka: centred over each block's
 *   headline — the chat's (inside its card, so over the page there), the
 *   map's, the posts' — riding its slot as the page scrolls and floating on to
 *   wait for the next, back in his corner through the tiles between the chat
 *   and the map (`kaSlotAt`, shared with desktop).
 * - At the close the desk Ka takes over on the spot where his head is — never
 *   two at once — and the FAQ and footer are Ka-free.
 *
 * Only ever in one anchor, never in between. A move the reader can see (Ka
 * and his next spot both on screen) is a glide; any other is a fade in place.
 * His canvas is never clipped.
 */
import {
  animate,
  type AnimationPlaybackControls,
  motion,
  type MotionValue,
  useMotionValue,
  useMotionValueEvent,
  useTransform,
} from 'framer-motion';
import { type RefObject, useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

import {
  EASE_OUT,
  FloatingKa,
  KA_BOX,
  KA_FLOAT,
  KA_GLIDE,
  KA_SMALL,
  setKaLanded,
  useKaInCard,
} from './landing-ka';
import { KA_TRAVEL_START, kaSlotAt, type KaTravelState } from './landing-ka-travel';
import { PHONE_BAR_HEIGHT, PHONE_BAR_TOP } from './landing-phone-bar';

/** Ka's corner (Figma 116:901): 64px, 20px in from the right and the bottom. */
const CORNER_INSET = 20;
/** The reading band on a phone: under the top bar, and above the floating
 *  button (24px off the bottom, 56px tall) with 16px to spare. */
const BAND = { top: PHONE_BAR_TOP + PHONE_BAR_HEIGHT + 16, bottomInset: 24 + 56 + 16 };

/** An element's place in the document, by layout (an entrance's lift doesn't
 *  move it). */
function layoutBox(el: HTMLElement) {
  let left = 0;
  let top = 0;
  for (
    let node: HTMLElement | null = el;
    node;
    node = node.offsetParent as HTMLElement | null
  ) {
    left += node.offsetLeft;
    top += node.offsetTop;
  }
  // `over`: the slot sits inside a card (the agent chat), where Ka is drawn
  // over the page rather than under it.
  return {
    left,
    top,
    width: el.offsetWidth,
    over: el.hasAttribute('data-lv3-ka-over'),
    id: el.dataset.lv3Ka ?? null,
  };
}

/** Ka's spot on screen (his card's top-left) and size. */
export type KaSpot = { x: number; y: number; size: number };

export function PhoneKa({
  contentRef,
  lineSpot,
  scrollY,
  layoutTick,
  deskShown,
  reduceMotion,
  onCornerChange,
}: {
  /** The page's content, whose slots Ka sits in. */
  contentRef: RefObject<HTMLElement | null>;
  /** Over the hero's line while he speaks it; null otherwise. */
  lineSpot: KaSpot | null;
  scrollY: MotionValue<number>;
  /** Bumped whenever the page lays out again. */
  layoutTick: MotionValue<number>;
  /** Set to 1 when the close's desk Ka takes over from this one. */
  deskShown: MotionValue<number>;
  reduceMotion: boolean;
  /** Ka is (or stops being) in his corner, beside the floating button. */
  onCornerChange: (inCorner: boolean) => void;
}) {
  // Where Ka is = his anchor's spot, set directly (a slot anchor moves exactly
  // with the page), plus a glide offset that springs back to zero after each
  // change of anchor — so gliding onto a block that is scrolling never chases
  // it.
  const bx = useMotionValue(0);
  const by = useMotionValue(0);
  const ox = useMotionValue(0);
  const oy = useMotionValue(0);
  const scale = useMotionValue(KA_SMALL / KA_BOX);
  // Plain values written on every change of their parts (as desktop's): a
  // computed transform can miss a same-value write and leave Ka at 0,0.
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const syncX = () => x.set(bx.get() + ox.get());
  const syncY = () => y.set(by.get() + oy.get());
  useMotionValueEvent(bx, 'change', syncX);
  useMotionValueEvent(ox, 'change', syncX);
  useMotionValueEvent(by, 'change', syncY);
  useMotionValueEvent(oy, 'change', syncY);
  const presence = useMotionValue(0);
  const presenceScale = useTransform(presence, (p) => 1 - 0.15 * (1 - p));
  // Invisible, Ka is parked far off screen: an opacity-0 canvas still draws
  // every frame, but off screen the runtime stops it.
  const park = useTransform(presence, (p) => (p < 0.004 ? -100_000 : 0));

  const anchor = useRef<string | null>(null);
  const glide = useRef<AnimationPlaybackControls[]>([]);
  const fade = useRef<{ target: number; controls?: AnimationPlaybackControls }>({
    target: -1,
  });
  const travel = useRef<KaTravelState>(KA_TRAVEL_START);
  const slots = useRef<ReturnType<typeof layoutBox>[]>([]);
  const line = useRef(lineSpot);
  line.current = lineSpot;
  /** In his corner, over the hero for his line, in a card's slot (over the
   *  page), or in the page's (under it). */
  const [mode, setMode] = useState<'corner' | 'line' | 'over' | 'page'>('corner');
  useEffect(() => onCornerChange(mode === 'corner'), [mode, onCornerChange]);

  const show = (on: boolean) => {
    const target = on ? 1 : 0;
    if (fade.current.target === target) return;
    fade.current.controls?.stop();
    fade.current = {
      target,
      controls: animate(
        presence,
        target,
        reduceMotion ? { duration: 0 } : { duration: 0.3, ease: EASE_OUT },
      ),
    };
  };

  const place = (
    id: string,
    px: number,
    py: number,
    size: number,
    transition: typeof KA_GLIDE | typeof KA_FLOAT,
    /** The slot's `data-lv3-ka` id: Ka has landed there once he arrives. */
    lands: string | null = null,
  ) => {
    if (id === anchor.current) {
      bx.set(px);
      by.set(py);
      return;
    }
    const from = { x: x.get(), y: y.get(), s: scale.get() };
    bx.set(px);
    by.set(py);
    const w = window.innerWidth;
    const h = window.innerHeight;
    const whollyOn = (ax: number, ay: number, s: number) =>
      ay >= 0 && ay + KA_BOX * s <= h && ax >= 0 && ax + KA_BOX * s <= w;
    const partlyOn = (ax: number, ay: number, s: number) =>
      ay < h && ay + KA_BOX * s > 0 && ax < w && ax + KA_BOX * s > 0;
    const target = size / KA_BOX;
    const first = anchor.current === null;
    anchor.current = id;
    glide.current.forEach((a) => a.stop());
    glide.current = [];
    setKaLanded(null);
    const visible = presence.get() >= 0.05;
    if (
      !first &&
      !reduceMotion &&
      visible &&
      whollyOn(from.x, from.y, from.s) &&
      whollyOn(px, py, target)
    ) {
      ox.jump(from.x - px);
      oy.jump(from.y - py);
      // A float's length sets its time, as on desktop: about 600px a second,
      // never under 0.9s or over 1.9s.
      const t =
        transition === KA_FLOAT
          ? {
              ...KA_FLOAT,
              duration: Math.min(
                1.9,
                Math.max(0.9, Math.hypot(from.x - px, from.y - py) / 600),
              ),
            }
          : transition;
      glide.current = [animate(ox, 0, t), animate(oy, 0, t), animate(scale, target, t)];
      // Landed once the glide is over — unless he has been sent on since.
      if (lands) {
        void glide.current[0].then(() => {
          if (anchor.current === id) setKaLanded(lands);
        });
      }
      return;
    }
    ox.jump(0);
    oy.jump(0);
    scale.jump(target);
    setKaLanded(lands);
    // Arriving where the reader can see: out, and back in on the spot.
    if (!first && visible && partlyOn(px, py, target)) {
      fade.current.controls?.stop();
      presence.jump(0);
      fade.current = { target: 0 };
    }
  };

  const measure = () => {
    slots.current = [...document.querySelectorAll<HTMLElement>('[data-lv3-ka]')].map(
      layoutBox,
    );
  };

  const update = () => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    const sy = scrollY.get();
    const rects = slots.current.map((s) => ({
      left: s.left,
      top: s.top - sy,
      width: s.width,
    }));
    const pick = kaSlotAt(rects, { ...BAND, height: h }, travel.current, {
      parkGap: 1.5 * h,
    });
    travel.current = pick;
    // At the close the desk Ka stands in: this one fades out where his head
    // is, and the two are never shown together.
    deskShown.set(pick.final ? 1 : 0);
    const spot = pick.slot < 0 ? line.current : null;
    const inCard = pick.slot >= 0 && !pick.floating && slots.current[pick.slot]?.over;
    setMode(spot ? 'line' : pick.slot < 0 ? 'corner' : inCard ? 'over' : 'page');
    if (spot) {
      place('line', spot.x, spot.y, spot.size, KA_GLIDE);
    } else if (pick.slot < 0) {
      place(
        'corner',
        w - CORNER_INSET - KA_SMALL,
        h - CORNER_INSET - KA_SMALL,
        KA_SMALL,
        KA_GLIDE,
      );
    } else if (pick.floating) {
      // Waiting for the next block at his own size, not the next slot's: on a
      // phone the desk Ka's 120px slot would loom over the posts' button. Its
      // own anchor, so reaching a bigger slot is a glide, not a jump.
      const y = Math.min(
        Math.max(rects[pick.slot].top, BAND.top),
        h - BAND.bottomInset - KA_SMALL,
      );
      place(
        `wait-${pick.slot}`,
        pick.x + (pick.size - KA_SMALL) / 2,
        y,
        KA_SMALL,
        KA_FLOAT,
      );
    } else {
      place(
        `slot-${pick.slot}`,
        pick.x,
        pick.y,
        pick.size,
        KA_FLOAT,
        slots.current[pick.slot]?.id ?? null,
      );
    }
    // A card drawing its own Ka (the oral exam) has him: this one steps out.
    show(!pick.final && !(kaInCardRef.current && pick.slot < 0 && !spot));
  };

  const kaInCard = useKaInCard();
  const kaInCardRef = useRef(kaInCard);
  kaInCardRef.current = kaInCard;

  // Mounted after the first paint, so the hero never waits on the Rive file.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const timer = window.setTimeout(() => setMounted(true), 600);
    return () => window.clearTimeout(timer);
  }, []);

  useMotionValueEvent(scrollY, 'change', update);
  // The hero's line starting or ending moves him even without a scroll event.
  const lineKey = lineSpot ? `${lineSpot.x},${lineSpot.y},${lineSpot.size}` : '';
  useEffect(() => {
    if (mounted) update();
  }, [lineKey, mounted, kaInCard]);
  useMotionValueEvent(layoutTick, 'change', () => {
    measure();
    update();
  });
  useEffect(() => {
    if (!mounted) return;
    const relayout = () => {
      measure();
      update();
    };
    relayout();
    // The blocks below the features grow as they arrive (the posts row mounts
    // on arrival), which moves every slot after them; the screen resizing
    // moves his corner.
    const observer = new ResizeObserver(relayout);
    if (contentRef.current) observer.observe(contentRef.current);
    window.addEventListener('resize', relayout);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', relayout);
    };
  }, [mounted, contentRef]);

  if (!mounted) return null;
  return (
    <motion.div
      aria-hidden
      // In his corner, over the hero and in the chat's card he is over the
      // page (the cards pass under him). From the map on he is under it, as
      // on desktop: a review, a post or a button passing his waiting spot is
      // never covered by Ka.
      className={cn(
        'pointer-events-none fixed left-0 top-0 will-change-transform',
        mode === 'page' ? 'z-[3]' : 'z-[16]',
      )}
      style={{ x, y, scale, originX: 0, originY: 0, width: KA_BOX, height: KA_BOX }}>
      <motion.div
        style={{ opacity: presence, scale: presenceScale, x: park }}
        className="h-full w-full">
        <FloatingKa reduceMotion={reduceMotion} box={KA_BOX} />
      </motion.div>
    </motion.div>
  );
}
